import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useCookies } from 'react-cookie'

import {
  type WebsiteLocale,
  ALL_WEBSITE_LOCALES,
  BASE_WEBSITE_LOCALES,
  COUNTRY_DISPLAY_BY_CODE,
  WEBSITE_DEFAULT_LOCALE,
} from '@/types/locales'

import { useTrackedRouter } from '@/lib/attribution/useTrackedRouter'
import { CHECKOUT_COUNTRIES } from '@/lib/checkout/types'
import { getPathWithoutAnyLocale, hasAnyLocale } from '@/lib/utils/localisationUtils'

interface LocaleContextType {
  currentLocale: WebsiteLocale
  baseLocale: WebsiteLocale
  setLocale: (locale: WebsiteLocale) => void
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined)

const LOCALE_COOKIE_KEY = 'cPreferredLocale'

const PERSISTED_LOCALES: readonly WebsiteLocale[] = CHECKOUT_COUNTRIES.map(
  (country) => COUNTRY_DISPLAY_BY_CODE[country].locale,
)

export const LocaleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const router = useTrackedRouter()
  const [cookies, setCookie] = useCookies([LOCALE_COOKIE_KEY])
  const [currentLocale, setCurrentLocale] = useState<WebsiteLocale>(WEBSITE_DEFAULT_LOCALE)
  const [baseLocale, setBaseLocale] = useState<WebsiteLocale>(WEBSITE_DEFAULT_LOCALE)

  // Ref so the effect can read the latest cookie value without subscribing to cookie changes.
  // Including `cookies` in the effect deps causes a feedback loop: setLocaleCookie → cookies
  // change → effect re-runs with the stale router.query.locale (old URL, before navigation
  // completes) → currentLocale briefly reverts to the old locale mid-navigation.
  const storedLocaleRef = useRef<string>(cookies[LOCALE_COOKIE_KEY])
  storedLocaleRef.current = cookies[LOCALE_COOKIE_KEY]

  const setLocaleCookie = useCallback(
    (locale: WebsiteLocale) => {
      try {
        setCookie(LOCALE_COOKIE_KEY, locale, {
          path: '/',
          maxAge: 30 * 24 * 60 * 60, // 30 days
          sameSite: 'lax',
        })
      } catch (error) {
        console.error('Error setting cookie:', error)
      }
    },
    [setCookie],
  )

  // Set initial locale from URL when router is ready, or use stored preference.
  // cookies/setCookie are intentionally excluded from deps — see storedLocaleRef above.
  // EU locales are page-scoped (daily30 only) and are never persisted to the cookie —
  // they are always re-derived from geo via the middleware URL redirect.
  useEffect(() => {
    if (router.isReady) {
      const localeFromUrl = router.query.locale as WebsiteLocale
      if (localeFromUrl && ALL_WEBSITE_LOCALES.includes(localeFromUrl)) {
        setCurrentLocale(localeFromUrl)
        if (BASE_WEBSITE_LOCALES.includes(localeFromUrl)) setBaseLocale(localeFromUrl)
        if (PERSISTED_LOCALES.includes(localeFromUrl)) setLocaleCookie(localeFromUrl)
      } else {
        // Use stored preference if no locale in URL
        const stored = storedLocaleRef.current
        const resolved = ALL_WEBSITE_LOCALES.includes(stored as WebsiteLocale)
          ? (stored as WebsiteLocale)
          : WEBSITE_DEFAULT_LOCALE
        setCurrentLocale(resolved)
        if (BASE_WEBSITE_LOCALES.includes(resolved)) {
          setBaseLocale(resolved)
        }
      }
    }
  }, [router.isReady, router.query.locale])

  const setLocale = useCallback(
    (newLocale: WebsiteLocale) => {
      if (newLocale !== currentLocale && router.isReady) {
        setCurrentLocale(newLocale)
        if (BASE_WEBSITE_LOCALES.includes(newLocale)) setBaseLocale(newLocale)
        if (PERSISTED_LOCALES.includes(newLocale)) setLocaleCookie(newLocale)

        // Use a full navigation so middleware runs and the locale in the URL is
        // unambiguous — router.push on dynamic-route pages produces ?locale=
        // query params instead of updating the path segment.
        if (hasAnyLocale(router.asPath)) {
          const pathWithoutLocale = getPathWithoutAnyLocale(router.asPath)
          window.location.assign(`/${newLocale}${pathWithoutLocale}`)
        }
      }
    },
    [currentLocale, router.isReady, router.asPath, setLocaleCookie],
  )

  const contextValue = useMemo(() => ({ currentLocale, baseLocale, setLocale }), [currentLocale, baseLocale, setLocale])

  return <LocaleContext.Provider value={contextValue}>{children}</LocaleContext.Provider>
}

export const useLocale = () => {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocale must be used within LocaleProvider')
  }
  return context
}
